import { useCallback, useEffect, useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useConversationUuid } from '~/hooks/conversation/useConversationUuid'
import { useTaskDetails } from '~/hooks/task/useTaskDetails'
import { AgentTaskStatus, MigrationSelectedEndpoint } from '~/protos/agents'
import {
  submitMigrationEndpoints,
  submitRefactorFeedback,
  submitRefactorUserComments
} from '~/services/taskService'
import { BetterToast } from '~/utils/toast'
import { MuleRefactorFlowLayout } from './MuleRefactorFlowLayout'
import {
  MuleRefactorFlowStep,
  deriveStepFromTask,
  getSlideConfigById,
  slideIndexOf,
  stepNumberOf
} from './slideConfig'
import { Artifact1LoadingSlide } from './slides/Artifact1LoadingSlide'
import { Artifact2CodeSlide } from './slides/Artifact2CodeSlide'
import { EndpointSelectionSlide } from './slides/EndpointSelectionSlide'
import { ErrorSlide } from './slides/ErrorSlide'
import { SourceFilesLoadingSlide } from './slides/SourceFilesLoadingSlide'
import { UploadInstructionsSlide } from './slides/UploadInstructionsSlide'

interface SelectedEndpoint {
  id: string
  name: string
  category?: string
  filePath?: string
}

interface MuleRefactorFlowScreenProps {
  taskStatus: AgentTaskStatus
  taskId: number
}

export function MuleRefactorFlowScreen({ taskStatus, taskId }: MuleRefactorFlowScreenProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const conversationUuid = useConversationUuid()
  const swiperRef = useRef<SwiperType | null>(null)

  const [selectedEndpoints, setSelectedEndpoints] = useState<SelectedEndpoint[]>([])
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Local optimistic override for "Proceed" clicks — cleared once taskStatus
  // catches up. This is the only piece of slide state we don't derive.
  const [optimisticStep, setOptimisticStep] = useState<MuleRefactorFlowStep | null>(null)

  const { data: taskDetailsData } = useTaskDetails(conversationUuid, taskId.toString())
  const task = taskDetailsData?.task

  const derivedStep = deriveStepFromTask(taskStatus, task)
  const currentStep: MuleRefactorFlowStep =
    optimisticStep && stepNumberOf(derivedStep) < stepNumberOf(optimisticStep)
      ? optimisticStep
      : derivedStep
  const currentStepNumber = stepNumberOf(currentStep)
  const slideIndex = slideIndexOf(currentStep)

  // Reset optimistic state when switching to a different task.
  useEffect(() => {
    setOptimisticStep(null)
  }, [taskId])

  // Clear optimistic override once real status catches up.
  useEffect(() => {
    if (optimisticStep && stepNumberOf(derivedStep) >= stepNumberOf(optimisticStep)) {
      setOptimisticStep(null)
    }
  }, [derivedStep, optimisticStep])

  // Swiper owns its own internal `activeIndex`; sync only when ours diverges.
  useEffect(() => {
    if (swiperRef.current && swiperRef.current.activeIndex !== slideIndex) {
      swiperRef.current.slideTo(slideIndex)
    }
  }, [slideIndex])

  const handleSelectionChange = useCallback((_count: number, endpoints: SelectedEndpoint[]) => {
    setSelectedEndpoints(endpoints)
  }, [])

  const handleProceed = async () => {
    if (!currentWorkspace?.uuid || !conversationUuid) {
      BetterToast.error('Missing required information')
      return
    }

    setIsSubmitting(true)
    try {
      switch (currentStep) {
        case 'selectEndpoints': {
          const migrationEndpoints: MigrationSelectedEndpoint[] = selectedEndpoints.map(ep => ({
            category: ep.category || '',
            name: ep.name,
            filePath: ep.filePath || ''
          }))
          await submitMigrationEndpoints({
            workspaceUuid: currentWorkspace.uuid,
            uuid: conversationUuid,
            taskId,
            selectedEndpoints: migrationEndpoints
          })
          setOptimisticStep('getReady')
          break
        }
        case 'getReady':
          // UI-only transition; backend doesn't track this step.
          setOptimisticStep('uploadInstructions')
          break
        case 'uploadInstructions':
          await submitRefactorUserComments(currentWorkspace.uuid, conversationUuid, taskId, {
            generalComments: additionalInstructions,
            skipPrcApi: false
          })
          setOptimisticStep('refineFiles')
          break
        case 'refineFiles':
          await submitRefactorFeedback(currentWorkspace.uuid, conversationUuid, taskId, '')
          setOptimisticStep('getCode')
          break
      }
    } catch (error) {
      console.error('Failed to proceed:', error)
      BetterToast.error('Failed to proceed to next step')
    } finally {
      setIsSubmitting(false)
    }
  }

  const slideConfig = getSlideConfigById(currentStep)
  const isProceedDisabled = !slideConfig?.isButtonEnabled?.({
    taskStatus,
    selectedEndpointsCount: selectedEndpoints.length,
    isSubmitting
  })

  return (
    <MuleRefactorFlowLayout
      currentStepNumber={currentStepNumber}
      taskId={taskId}
      proceedButtonText={slideConfig?.buttonText || 'Proceed'}
      isProceedDisabled={isProceedDisabled}
      isProceedLoading={isSubmitting}
      onProceed={handleProceed}
    >
      <Swiper
        onSwiper={swiper => {
          swiperRef.current = swiper
        }}
        initialSlide={slideIndex}
        allowTouchMove={false}
        spaceBetween={0}
        slidesPerView={1}
        className='w-full h-full'
      >
        <SwiperSlide>
          <EndpointSelectionSlide
            taskStatus={taskStatus}
            onSelectionChange={handleSelectionChange}
          />
        </SwiperSlide>
        <SwiperSlide>
          <SourceFilesLoadingSlide taskStatus={taskStatus} />
        </SwiperSlide>
        <SwiperSlide>
          <UploadInstructionsSlide onInstructionsChange={setAdditionalInstructions} />
        </SwiperSlide>
        <SwiperSlide>
          <Artifact1LoadingSlide taskStatus={taskStatus} />
        </SwiperSlide>
        <SwiperSlide>
          <Artifact2CodeSlide taskStatus={taskStatus} />
        </SwiperSlide>
        <SwiperSlide>
          <ErrorSlide />
        </SwiperSlide>
      </Swiper>
    </MuleRefactorFlowLayout>
  )
}
